import GameManager from '../GameManager';
import Tools from '../Misc/Tools';

/**
 * Manages the UI
 */
export default class UIManager {
	/**
	 * Gets all UIs
	 */
	public get uis(): UI[] {
		return this._uis;
	}
	/**
	 * All registered UIs
	 */
	public _uis: UI[] = [];

	/**
	 * ctor
	 * @param gameManager A reference to the Game Manager
	 */
	constructor(public gameManager: GameManager) {
		this._uis = [];
	}

	/**
	 * Creates a new instance of a UI
	 * @param uiName The name of the UI to create
	 * @param parameters All parameters to use for this UI
	 * @returns The instance ID of the instance that was just created
	 */
	public createUIInstance<UIParamsType>(uiName: string, parameters?: UIParamsType): string {
		var ui = this.getUIByName(uiName);

		if (!ui) {
			this.gameManager.log('Could not find UI with name ' + uiName);
			return;
		}

		var params = parameters ? { ...parameters, isVisible: true } : { isVisible: true, parameters: {} };

		var instance: UIInstance = {
			id: Tools.generateGuid(),
			parameters: params
		};

		if(!instance.parameters) {
			instance.parameters = {};
		}

		ui.instances.push(instance);

		return instance.id;
	}

	/**
	 * Updates a UI
	 * @param instanceId The ID that was returned when creating the UI instance you want to update
	 * @param parameters All parameters to use for this UI. You only have to supply the parameters that are meant to be updated. No other parameters will be overwritten.
	 */
	public updateUIInstance<UIParamsType>(instanceId: string, parameters?: UIParamsType): boolean {
		var instance = this.getInstanceById(instanceId);

		if (!instance) {
			this.gameManager.log('Could not find instance with id ' + instanceId);
			return false;
		}

		var params = parameters ? { ...parameters, isVisible: true } : { isVisible: true, parameters: {} };
		if(!params) {
			params = ({}) as any;
		}

		for(var prop in params) {
			instance.parameters[prop] = (params as any)[prop];
		}
		if(instance.parameters.onForceUpdate) {
			instance.parameters.onForceUpdate();
		}

		return true;
	}

	/**
	 * Hides all UIs currently visible. You can still reference these UIs by using their instance IDs as they will still be in memory
	 * @param tagExceptions Optional: Tags for ui's that will not be touched
	 */
	public hideAllUis(tagExceptions: string[]): void {
		for (var uiIndx = 0; uiIndx < this._uis.length; uiIndx++) {
			var ui = this._uis[uiIndx];

			if(ui.tag && tagExceptions && tagExceptions.indexOf(ui.tag) !== -1) {
				continue;
			}

			for (var instanceIndx = 0; instanceIndx < ui.instances.length; instanceIndx++) {
				ui.instances[instanceIndx].parameters.isVisible = false;
			}
		}
	}

	/**
	 * Shows all UIs currently hidden.
	 * @param tagExceptions Optional: Tags for ui's that will not be touched
	 */
	 public showAllUis(tagExceptions: string[]): void {
		for (var uiIndx = 0; uiIndx < this._uis.length; uiIndx++) {
			var ui = this._uis[uiIndx];

			if(ui.tag && tagExceptions && tagExceptions.indexOf(ui.tag) !== -1) {
				continue;
			}

			for (var instanceIndx = 0; instanceIndx < ui.instances.length; instanceIndx++) {
				ui.instances[instanceIndx].parameters.isVisible = true;
			}
		}
	}

	/**
	 * Shows a single UI instance
	 * @param uiName The name of the UI
	 * @param instanceId The ID of the instance to show
	 */
	public showUiInstance(instanceId: string): void {
		var instance = this.getInstanceById(instanceId);
		if (!instance) {
			this.gameManager.log('Could not find instance with id ' + instanceId);
			return;
		}

		instance.parameters.isVisible = true;
	}

	/**
	 * Hides a single UI instance. You can still reference this UI by using its instance ID as it will still be in memory
	 * @param instanceId The ID of the instance to hide
	 */
	public hideUiInstance(instanceId: string): void {
		var instance = this.getInstanceById(instanceId);
		if (!instance) {
			this.gameManager.log('Could not find instance with id ' + instanceId);
			return;
		}

		instance.parameters.isVisible = false;
	}

	/**
	 * Destroys all UI instances for UI that have a specific tag. Instances will no longer live in memory so instance IDs will no longer exist
	 * @param tags Tags for ui's that will have their instances destroyed
	 */
	 public destroySpecificInstances(tags: string[]): void {
		for (var uiIndx = 0; uiIndx < this._uis.length; uiIndx++) {
			var ui = this._uis[uiIndx];

			if(!ui.tag || (ui.tag && tags && tags.indexOf(ui.tag) === -1)) {
				continue;
			}

			ui.instances = [];
		}
	}

	/**
	 * Destroys all UI instances. Instances will no longer live in memory so instance IDs will no longer exist
	 * @param tagExceptions Optional: Tags for ui's that will not be destroyed
	 */
	public destroyAllInstances(tagExceptions?: string[]): void {
		for (var uiIndx = 0; uiIndx < this._uis.length; uiIndx++) {
			var ui = this._uis[uiIndx];

			if(ui.tag && tagExceptions && tagExceptions.indexOf(ui.tag) !== -1) {
				continue;
			}

			// if(ui.name === 'ToolbagUI') { //use this to debug if UIs are being destroyed unexpectedly
			// 	debugger
			// }

			ui.instances = [];
		}
	}

	/**
	 * Destroys a single instance for a specific UI. The instance will no longer live in memory so instance ID will no longer exist
	 * @param uiName The name of the UI to destroy the instance for
	 * @param instanceId The ID of the instance
	 */
	public destroyUIInstanceById(instanceId: string): void {
		var instanceIndex = -1;

		for (var uiIndx = 0; uiIndx < this._uis.length; uiIndx++) {
			var ui = this._uis[uiIndx];

			for (var i = 0; i < ui.instances.length; i++) {
				var ins = ui.instances[i];
	
				if (ins.id === instanceId) {
					instanceIndex = i;
					break;
				}
			}

			if(instanceIndex !== -1) {
				break;
			}
		}

		if (instanceIndex === -1) {
			this.gameManager.log('Could not find instance with id ' + instanceId + ' inside of ui with name ' + ui.name);
			return;
		}

		ui.instances.splice(instanceIndex, 1);
	}

	/**
	 * Registers a single UI
	 * @param uiName The name of the UI to register
	 * @param uiData Arbitrary data to append to the UI, if any
	 * @param tag Optional: Use this to group UIs. Example: 'HUD'
	 * @returns true if it was successfully registered
	 */
	public registerUI(uiName: string, uiData?: any, tag?: string): boolean {
		var ui = this.getUIByName(uiName);
		if (ui) {
			this.gameManager.log('There is already a UI with name ' + uiName);
			return false;
		}

		this._uis.push({
			name: uiName,
			uiData: uiData,
			instances: [],
			tag: tag
		});

		return true;
	}

	/**
	 * Unregisters a particular UI
	 * @param uiName The name of the UI
	 */
	public unregisterUI(uiName: string): void {
		var uiIndx = -1;

		for (var i = 0; i < this._uis.length; i++) {
			var ui = this._uis[i];

			if (ui.name === uiName) {
				uiIndx = i;
				break;
			}
		}

		if (uiIndx === -1) {
			this.gameManager.log('Could not find UI with name ' + uiName);
			return;
		}

		this._uis.splice(uiIndx, 1);
	}

	/**
	 * Unregisters all UIs. You will have to call registerUI again
	 */
	 public unregisterAll(): void {
		this._uis = [];
	}

	/**
	 * Renders all the UIs.
	 */
	public render(): any {
    	throw new Error('render must be implemented');
	}

	/**
	 * Gets a single UI by its name
	 * @param uiName The name of the UI
	 * @returns the retrieved UI
	 */
	public getUIByName(uiName: string): UI {
		for (var uiIndx = 0; uiIndx < this._uis.length; uiIndx++) {
			var ui = this._uis[uiIndx];

			if (ui.name === uiName) {
				return ui;
			}
		}

		return undefined;
	}

	/**
	 * Gets a single instance by its UI and instance ID
	 * @param instanceId The instance ID to use
	 */
	public getInstanceById(instanceId: string): UIInstance {
		for (var uiIndx = 0; uiIndx < this._uis.length; uiIndx++) {
			var ui = this._uis[uiIndx];
			for (var instanceIndx = 0; instanceIndx < ui.instances.length; instanceIndx++) {
				var instance = ui.instances[instanceIndx];

				if (instance.id === instanceId) {
					return instance;
				}
			}
		}

		return undefined;
	}
}

/**
 * Describes a single UI
 */
export interface UI {
	/**
	 * The name of the UI. This must be unique
	 */
	name: string;

	/**
	 * Arbitrary UI data
	 */
	uiData: any;

	/**
	 * All instances for this particular UI
	 */
	instances: UIInstance[];

	/**
	 * Tag used to group UIs: Example: 'HUD'
	 */
	 tag?: any;
}

/**
 * Describes a single instance of the UI
 */
export interface UIInstance {
	/**
	 * A unique ID for the UI
	 */
	id: string;

	/**
	 * For any parameters pertaining to the UI
	 */
	parameters: any;
}